import {
  classifyAbortLikeError,
  getRuntimeBuildIdentity,
  recordRuntimeDiagnosticEvent,
} from './runtimeDiagnostics.ts';

const CHUNK_RECOVERY_STORAGE_KEY = 'mintleaf.chunk-load-recovery.v1';
const CHUNK_RECOVERY_QUERY_PARAM = 'mintleafChunkReload';
const CHUNK_RECOVERY_PROBE_QUERY_PARAM = 'mintleafRuntimeProbe';
const CHUNK_RECOVERY_COOLDOWN_MS = 60_000;
const CHUNK_RECOVERY_NAVIGATION_FALLBACK_MS = 5_000;
const CHUNK_RECOVERY_REPAIR_VERSION = 2;
const CHUNK_RECOVERY_OVERLAY_ID = 'mintleaf-chunk-load-recovery';

const CHUNK_LOAD_ERROR_PATTERN = /(?:failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|failed to load module script|chunkloaderror|loading chunk\s+[^\s]+\s+failed|unable to preload css)/i;
const HTML_CONTENT_TYPE_PATTERN = /(?:text\/html|application\/xhtml\+xml)/i;
const JAVASCRIPT_CONTENT_TYPE_PATTERN = /(?:javascript|ecmascript)/i;
const FIREBASE_MESSAGING_SERVICE_WORKER_PATTERN = /\/firebase-messaging-sw\.(?:m?js)$/i;
const RUNTIME_PROBE_DELAYS_MS = [0, 300, 900] as const;

type ChunkRecoveryRecord = {
  attemptedAt: number;
  buildId: string;
  failedAssetPath: string | null;
  repairVersion: number;
};

type ChunkRecoverySource =
  | 'vite:preloadError'
  | 'unhandledrejection'
  | 'window.error';

type VitePreloadErrorEvent = Event & {
  payload?: unknown;
};

declare global {
  interface Window {
    __MINTLEAF_CHUNK_LOAD_RECOVERY_REGISTERED__?: boolean;
  }
}

let recoveryNavigationScheduled = false;

const readErrorText = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (!error || typeof error !== 'object') return String(error ?? '');

  const record = error as { name?: unknown; message?: unknown; stack?: unknown };
  return [record.name, record.message, record.stack]
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    .join(' ');
};

const extractFailedAssetPath = (error: unknown): string | null => {
  const text = readErrorText(error);
  const absoluteUrlMatch = text.match(/https?:\/\/[^\s)'"<>]+/i);
  const pathMatch = text.match(/\/assets\/[^\s)'"<>]+/i);
  const candidate = absoluteUrlMatch?.[0] || pathMatch?.[0];
  if (!candidate) return null;

  try {
    return new URL(candidate, window.location.origin).pathname;
  } catch {
    return candidate.split('?')[0].split('#')[0];
  }
};

const readRecoveryRecord = (): ChunkRecoveryRecord | null => {
  try {
    const raw = window.sessionStorage.getItem(CHUNK_RECOVERY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ChunkRecoveryRecord>;
    if (typeof parsed.attemptedAt !== 'number' || !Number.isFinite(parsed.attemptedAt)) return null;
    return {
      attemptedAt: parsed.attemptedAt,
      buildId: typeof parsed.buildId === 'string' ? parsed.buildId : 'unknown',
      failedAssetPath: typeof parsed.failedAssetPath === 'string' ? parsed.failedAssetPath : null,
      repairVersion: typeof parsed.repairVersion === 'number' && Number.isFinite(parsed.repairVersion)
        ? parsed.repairVersion
        : 1,
    };
  } catch {
    return null;
  }
};

const writeRecoveryRecord = (record: ChunkRecoveryRecord): void => {
  try {
    window.sessionStorage.setItem(CHUNK_RECOVERY_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Recovery must still work when sessionStorage is blocked.
  }
};

const clearRecoveryRecord = (): void => {
  try {
    window.sessionStorage.removeItem(CHUNK_RECOVERY_STORAGE_KEY);
  } catch {
    // no-op
  }
};

export const isChunkLoadError = (error: unknown): boolean =>
  CHUNK_LOAD_ERROR_PATTERN.test(readErrorText(error));

export const isChunkRecoveryCoolingDown = (
  previousAttemptAt: number | null | undefined,
  now = Date.now()
): boolean =>
  typeof previousAttemptAt === 'number' &&
  Number.isFinite(previousAttemptAt) &&
  now - previousAttemptAt >= 0 &&
  now - previousAttemptAt < CHUNK_RECOVERY_COOLDOWN_MS;

export const buildChunkRecoveryUrl = (href: string, timestamp = Date.now()): string => {
  const url = new URL(href);
  url.searchParams.set(CHUNK_RECOVERY_QUERY_PARAM, String(timestamp));
  url.searchParams.delete(CHUNK_RECOVERY_PROBE_QUERY_PARAM);
  return url.toString();
};

export const extractModuleEntryUrlFromHtml = (html: string, baseUrl: string): string | null => {
  const scriptTags = html.match(/<script\b[^>]*>/gi) || [];
  for (const tag of scriptTags) {
    if (!/\btype\s*=\s*["']module["']/i.test(tag)) continue;
    const source = tag.match(/\bsrc\s*=\s*["']([^"']+)["']/i)?.[1];
    if (!source) continue;
    try {
      return new URL(source, baseUrl).toString();
    } catch {
      return null;
    }
  }
  return null;
};

export const isUsableJavaScriptAssetResponse = (
  ok: boolean,
  contentType: string | null,
  assetUrl: string
): boolean => {
  if (!ok || HTML_CONTENT_TYPE_PATTERN.test(contentType || '')) return false;
  if (JAVASCRIPT_CONTENT_TYPE_PATTERN.test(contentType || '')) return true;
  try {
    return /\.m?js$/i.test(new URL(assetUrl).pathname);
  } catch {
    return /\.m?js(?:$|[?#])/i.test(assetUrl);
  }
};

const wait = (milliseconds: number): Promise<void> =>
  new Promise(resolve => window.setTimeout(resolve, milliseconds));

const listServiceWorkerScriptUrls = (registration: ServiceWorkerRegistration): string[] =>
  [registration.active?.scriptURL, registration.waiting?.scriptURL, registration.installing?.scriptURL]
    .filter((value): value is string => typeof value === 'string' && value.length > 0);

const shouldUnregisterServiceWorker = (registration: ServiceWorkerRegistration): boolean => {
  const scriptUrls = listServiceWorkerScriptUrls(registration);
  if (scriptUrls.length === 0) return true;
  return !scriptUrls.every(scriptUrl => FIREBASE_MESSAGING_SERVICE_WORKER_PATTERN.test(scriptUrl));
};

const clearStaleRuntimeCaches = async (source: ChunkRecoverySource | 'overlay-button'): Promise<void> => {
  const failures: string[] = [];

  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      const results = await Promise.allSettled(
        registrations
          .filter(shouldUnregisterServiceWorker)
          .map(registration => registration.unregister())
      );
      if (results.some(result => result.status === 'rejected')) failures.push('service-worker-unregister');
    } catch {
      failures.push('service-worker-list');
    }
  }

  if (typeof caches !== 'undefined') {
    try {
      const cacheNames = await caches.keys();
      const results = await Promise.allSettled(cacheNames.map(cacheName => caches.delete(cacheName)));
      if (results.some(result => result.status === 'rejected')) failures.push('cache-storage-delete');
    } catch {
      failures.push('cache-storage-list');
    }
  }

  recordRuntimeDiagnosticEvent({
    event: failures.length > 0
      ? 'runtime:chunk-recovery:client-cache-cleanup-partial'
      : 'runtime:chunk-recovery:client-cache-cleanup-complete',
    source,
    errorName: failures.length > 0 ? 'ChunkRecoveryCleanupError' : null,
    errorMessage: failures.length > 0 ? failures.join(',') : null,
  });
};

const buildRuntimeProbeUrl = (recoveryUrl: string, attempt: number): string => {
  const url = new URL(recoveryUrl);
  url.searchParams.set(CHUNK_RECOVERY_PROBE_QUERY_PARAM, `${Date.now()}-${attempt}`);
  return url.toString();
};

const probeFreshRuntime = async (recoveryUrl: string, attempt: number): Promise<boolean> => {
  const documentUrl = buildRuntimeProbeUrl(recoveryUrl, attempt);
  const documentResponse = await fetch(documentUrl, {
    method: 'GET',
    cache: 'reload',
    credentials: 'same-origin',
    redirect: 'follow',
    headers: { Accept: 'text/html,application/xhtml+xml' },
  });
  if (!documentResponse.ok || !HTML_CONTENT_TYPE_PATTERN.test(documentResponse.headers.get('content-type') || '')) {
    return false;
  }

  const html = await documentResponse.text();
  const entryUrl = extractModuleEntryUrlFromHtml(html, documentResponse.url || documentUrl);
  if (!entryUrl) return false;

  let entryResponse = await fetch(entryUrl, {
    method: 'HEAD',
    cache: 'reload',
    credentials: 'same-origin',
    redirect: 'follow',
    headers: { Accept: 'text/javascript,application/javascript,*/*;q=0.1' },
  });

  if (entryResponse.status === 405 || entryResponse.status === 501) {
    entryResponse = await fetch(entryUrl, {
      method: 'GET',
      cache: 'reload',
      credentials: 'same-origin',
      redirect: 'follow',
      headers: { Accept: 'text/javascript,application/javascript,*/*;q=0.1' },
    });
  }

  return isUsableJavaScriptAssetResponse(
    entryResponse.ok,
    entryResponse.headers.get('content-type'),
    entryResponse.url || entryUrl
  );
};

const waitForConsistentRuntime = async (recoveryUrl: string): Promise<boolean> => {
  for (let attempt = 0; attempt < RUNTIME_PROBE_DELAYS_MS.length; attempt += 1) {
    const delay = RUNTIME_PROBE_DELAYS_MS[attempt];
    if (delay > 0) await wait(delay);
    try {
      if (await probeFreshRuntime(recoveryUrl, attempt + 1)) return true;
    } catch {
      // The final navigation still runs even when the consistency probe is unavailable.
    }
  }
  return false;
};

const navigateToFreshRuntime = (
  error: unknown,
  source: ChunkRecoverySource | 'overlay-button'
): void => {
  if (recoveryNavigationScheduled) return;
  recoveryNavigationScheduled = true;

  const recoveryUrl = buildChunkRecoveryUrl(window.location.href);
  let navigationStarted = false;
  const navigate = (): void => {
    if (navigationStarted) return;
    navigationStarted = true;
    window.location.replace(recoveryUrl);
  };

  const fallbackTimer = window.setTimeout(navigate, CHUNK_RECOVERY_NAVIGATION_FALLBACK_MS);

  void (async () => {
    try {
      await clearStaleRuntimeCaches(source);
      const runtimeReady = await waitForConsistentRuntime(recoveryUrl);
      recordRuntimeDiagnosticEvent({
        event: runtimeReady
          ? 'runtime:chunk-recovery:fresh-runtime-confirmed'
          : 'runtime:chunk-recovery:fresh-runtime-unconfirmed',
        source,
        errorName: runtimeReady ? null : 'ChunkRecoveryProbeError',
        errorMessage: runtimeReady ? null : readErrorText(error) || null,
        scriptUrl: extractFailedAssetPath(error),
      });
    } finally {
      window.clearTimeout(fallbackTimer);
      navigate();
    }
  })();
};

const renderPersistentFailureOverlay = (error: unknown): void => {
  if (typeof document === 'undefined' || document.getElementById(CHUNK_RECOVERY_OVERLAY_ID)) return;

  const overlay = document.createElement('div');
  overlay.id = CHUNK_RECOVERY_OVERLAY_ID;
  overlay.setAttribute('role', 'alert');
  overlay.setAttribute('aria-live', 'assertive');
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '2147483647',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    background: 'rgba(15, 23, 42, 0.94)',
    color: '#f8fafc',
    fontFamily: 'system-ui, sans-serif',
  });

  const panel = document.createElement('div');
  Object.assign(panel.style, {
    width: 'min(440px, 100%)',
    borderRadius: '20px',
    padding: '24px',
    background: '#ffffff',
    color: '#0f172a',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.35)',
  });

  const title = document.createElement('h1');
  title.textContent = 'Az alkalmazás frissítése szükséges';
  Object.assign(title.style, { margin: '0 0 12px', fontSize: '20px', fontWeight: '800' });

  const description = document.createElement('p');
  description.textContent = 'Az automatikus helyreállítás nem tudott egységes alkalmazásverziót betölteni. A gomb kitisztítja az alkalmazás gyorsítótárát és friss fájlokkal indítja újra az oldalt. A bejelentkezési adatok nem törlődnek.';
  Object.assign(description.style, { margin: '0 0 20px', lineHeight: '1.5', color: '#475569' });

  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = 'Javítás és frissítés';
  Object.assign(button.style, {
    width: '100%',
    border: '0',
    borderRadius: '12px',
    padding: '12px 16px',
    background: '#0f172a',
    color: '#ffffff',
    fontWeight: '700',
    cursor: 'pointer',
  });
  button.addEventListener('click', () => {
    button.disabled = true;
    button.textContent = 'Javítás folyamatban…';
    button.style.cursor = 'wait';
    button.style.opacity = '0.8';
    clearRecoveryRecord();
    navigateToFreshRuntime(error, 'overlay-button');
  });

  const failedAssetPath = extractFailedAssetPath(error);
  if (failedAssetPath) {
    const detail = document.createElement('p');
    detail.textContent = `Nem betölthető fájl: ${failedAssetPath}`;
    Object.assign(detail.style, {
      margin: '16px 0 0',
      fontSize: '12px',
      color: '#64748b',
      overflowWrap: 'anywhere',
    });
    panel.append(title, description, button, detail);
  } else {
    panel.append(title, description, button);
  }

  overlay.appendChild(panel);
  document.body.appendChild(overlay);
};

const recoverFromChunkLoadFailure = (
  error: unknown,
  source: ChunkRecoverySource,
  force = false
): boolean => {
  if (!force && !isChunkLoadError(error)) return false;
  if (recoveryNavigationScheduled) return true;

  const now = Date.now();
  const previous = readRecoveryRecord();
  const failedAssetPath = extractFailedAssetPath(error);
  const identity = getRuntimeBuildIdentity();
  const sameBuildRepairCoolingDown =
    previous?.buildId === identity.buildId &&
    previous.repairVersion >= CHUNK_RECOVERY_REPAIR_VERSION &&
    isChunkRecoveryCoolingDown(previous.attemptedAt, now);

  if (sameBuildRepairCoolingDown) {
    recordRuntimeDiagnosticEvent({
      event: 'runtime:chunk-recovery:suppressed',
      source,
      errorName: 'ChunkLoadError',
      errorMessage: readErrorText(error) || null,
      scriptUrl: failedAssetPath,
    });
    renderPersistentFailureOverlay(error);
    return true;
  }

  writeRecoveryRecord({
    attemptedAt: now,
    buildId: identity.buildId,
    failedAssetPath,
    repairVersion: CHUNK_RECOVERY_REPAIR_VERSION,
  });
  recordRuntimeDiagnosticEvent({
    event: 'runtime:chunk-recovery:reload',
    source,
    errorName: 'ChunkLoadError',
    errorMessage: readErrorText(error) || null,
    scriptUrl: failedAssetPath,
  });

  navigateToFreshRuntime(error, source);
  return true;
};

export const registerChunkLoadRecovery = (): void => {
  if (typeof window === 'undefined' || window.__MINTLEAF_CHUNK_LOAD_RECOVERY_REGISTERED__) return;
  window.__MINTLEAF_CHUNK_LOAD_RECOVERY_REGISTERED__ = true;

  window.addEventListener('vite:preloadError', event => {
    const preloadEvent = event as VitePreloadErrorEvent;
    event.preventDefault();
    recoverFromChunkLoadFailure(
      preloadEvent.payload || new Error('Failed to load a dynamically imported module.'),
      'vite:preloadError',
      true
    );
  });

  window.addEventListener('unhandledrejection', event => {
    if (classifyAbortLikeError(event.reason).isAbortLike) {
      // Expected AbortController cancellations are already captured by runtime diagnostics.
      // Prevent the browser from surfacing them as red unhandled-rejection noise.
      event.preventDefault();
      return;
    }
    if (!isChunkLoadError(event.reason)) return;
    event.preventDefault();
    recoverFromChunkLoadFailure(event.reason, 'unhandledrejection');
  });

  window.addEventListener('error', event => {
    const errorEvent = event as ErrorEvent;
    const target = event.target as HTMLScriptElement | null;
    const targetSource = target && typeof target.src === 'string' ? target.src : '';
    const candidate = errorEvent.error || errorEvent.message || (
      targetSource.includes('/assets/')
        ? new Error(`Failed to load module script: ${targetSource}`)
        : null
    );
    if (!candidate || !isChunkLoadError(candidate)) return;
    event.preventDefault();
    recoverFromChunkLoadFailure(candidate, 'window.error');
  }, true);
};

export const __chunkRecoveryCooldownMs = CHUNK_RECOVERY_COOLDOWN_MS;
export const __chunkRecoveryStorageKey = CHUNK_RECOVERY_STORAGE_KEY;
export const __chunkRecoveryQueryParam = CHUNK_RECOVERY_QUERY_PARAM;
